<template>
    <div class="bg-white shadow rounded-lg mb-6">
        <div class="relative z-0 rounded-t-lg flex">
            <div
                data-test="one-time"
                @click="onSubscribe(false)"
                :class="[
                    !state.subscribe
                        ? 'text-nutreance-green font-semibold bg-emerald-50 border border-nutreance-green'
                        : 'text-gray-400 hover:text-gray-700 bg-white hover:bg-gray-50 border border-r-0 border-gray-200 cursor-pointer',
                    'rounded-tl-lg relative flex-1 overflow-hidden py-4 px-4 text-center flex justify-center items-center',
                ]"
            >
                <CheckCircleIcon
                    v-if="!state.subscribe"
                    class="h-5 w-5 mr-2"
                    aria-hidden="true"
                />
                One-Time
            </div>
            <div
                data-test="auto-delivery"
                @click="onSubscribe(true)"
                :class="[
                    state.subscribe
                        ? 'text-nutreance-green font-semibold bg-emerald-50 border border-nutreance-green'
                        : 'text-gray-400 hover:text-gray-700 bg-white hover:bg-gray-50 border border-l-0 border-gray-200 cursor-pointer',
                    'rounded-tr-lg relative flex-1 overflow-hidden py-4 px-4 text-center flex justify-center items-center',
                ]"
            >
                <CheckCircleIcon
                    v-if="state.subscribe"
                    class="h-5 w-5 mr-2"
                    aria-hidden="true"
                />
                Auto-Delivery
            </div>
        </div>

        <div
            class="px-4 py-5 sm:p-6 border border-t-0 border-gray-200 rounded-b-lg"
        >
            <p
                class="text-center px-3 py-1 text-sm text-red-600"
                v-if="errors.product_option_id"
            >
                {{ errors.product_option_id }}
            </p>

            <div class="grid grid-cols-4 md:block">
                <div class="md:hidden w-10 mx-auto">
                    <slot name="product-image" />
                </div>

                <div
                    class="col-span-3 flex items-center md:grid md:grid-cols-2 md:col-span-2 md:gap-x-4"
                >
                    <div class="mx-auto text-center">
                        <label class="block text-sm font-bold text-gray-700">
                            Size
                        </label>
                        <div class="mt-2 text-gray-700">
                            {{ product.default_product_option.description }}
                        </div>
                    </div>

                    <div class="mx-auto text-center">
                        <label class="block text-sm font-bold text-gray-700">
                            Price
                        </label>
                        <div class="mt-2 text-gray-700">
                            ${{
                                dollarsForDisplay(
                                    product.default_product_option.price,
                                )
                            }}
                        </div>
                    </div>
                </div>
            </div>

            <ProductQuantityAndSubscriptionOptionButtons
                class="mt-2 sm:my-4 md:mt-8 md:mb-0"
                @errorMaxQuantity="onErrorMaxQuantity"
                @quantityUpdated="onQuantityUpdated"
                @subscriptionOptionUpdated="onSubscriptionOptionUpdated"
                @unsubscribe="onSubscribe(false)"
                :errors="errors"
                :product="product"
                :subscribe="state.subscribe"
                :quantity="state.quantity"
                :subscriptionOptionId="state.subscriptionOptionId"
            />

            <div class="mt-4 md:mt-8 space-y-4">
                <div
                    v-if="!added && state.tips && state.tips.length > 0"
                    class="w-full p-4 focus:outline-none bg-amber-50 border border-amber-100 rounded-lg space-y-4"
                >
                    <button
                        type="button"
                        v-for="tip in state.tips"
                        :key="tip.id"
                        @click="
                            increaseQuantity(tip.recommended_quantity_change)
                        "
                        class="ml-3 text-left medium text-amber-700 hover:text-amber-800 flex items-center"
                    >
                        <LightBulbIcon
                            class="h-5 w-5 mr-2 text-amber-400 shrink-0"
                            aria-hidden="true"
                        />
                        {{ tip.name }}
                    </button>
                </div>

                <button
                    @click="addToCart"
                    v-if="
                        !added && state.discounts && state.discounts.length > 0
                    "
                    type="button"
                    class="w-full p-4 focus:outline-none bg-emerald-50 border border-emerald-100 rounded-lg"
                >
                    <span
                        class="block ml-3 text-left text-nutreance-green hover:text-nutreance-green-darker"
                    >
                        Click "Add to Cart" now to get:
                    </span>
                    <span
                        v-for="discount in state.discounts"
                        :key="discount.id"
                        class="mt-4 ml-3 text-left font-semibold text-nutreance-green hover:text-nutreance-green-darker flex items-center"
                    >
                        <CheckIcon
                            class="h-5 w-5 mr-2 text-nutreance-green shrink-0"
                            aria-hidden="true"
                        />
                        {{ discount.name }}
                    </span>
                </button>
            </div>

            <button
                @click="addToCart"
                name="add_to_cart"
                type="button"
                class="mt-4 md:mt-8 w-full py-3 shadow rounded-md text-base font-medium text-white bg-nutreance-green hover:bg-nutreance-green-darker focus:outline-none"
            >
                <span v-if="!added" class="flex items-center justify-center">
                    <LoadingIcon
                        v-if="loading"
                        class="h-5 w-5 mr-2 text-white"
                    />
                    <ShoppingCartIcon
                        v-else
                        class="h-5 w-5 mr-2"
                        aria-hidden="true"
                    />
                    Add to Cart
                </span>
                <span v-if="added" class="flex items-center justify-center">
                    <CheckCircleIcon class="h-5 w-5 mr-2" aria-hidden="true" />
                    Added {{ quantityAdded }}
                    {{ quantityAdded > 1 ? "bottles" : "bottle" }} to cart!
                </span>
            </button>
            <PhoneNumber class="text-center" :headquarters="headquarters" />
        </div>
    </div>
</template>

<script setup lang="ts">
import { inject, onMounted, reactive, ref } from "vue";
import {
    CheckIcon,
    CheckCircleIcon,
    ShoppingCartIcon,
} from "@heroicons/vue/solid";
import { LightBulbIcon } from "@heroicons/vue/outline";
import { useMoney } from "../../functions/useMoney";
import ProductQuantityAndSubscriptionOptionButtons from "./ProductQuantityAndSubscriptionOptionButtons.vue";
import PhoneNumber from "./PhoneNumber.vue";
import LoadingIcon from "../LoadingIcon.vue";
import { useGoogleDataLayer } from "../../functions/useGoogleDataLayer";
import { addCartItem } from "../../api/cart";
import { getProductOptions } from "../../api/products";
import type { AppEmitter } from "../../emitter";

const props = defineProps<{
    headquarters?: any;
    product?: any;
    recommendedProduct?: any;
}>();

const emitter = inject<AppEmitter>("mitt")!;

const { dollarsForDisplay } = useMoney();
const { pushAddToCartToDataLayer } = useGoogleDataLayer();
const loading = ref(false);
const added = ref(false);
const quantityAdded = ref(0);
const errors = reactive<Record<string, any>>({});
const state = reactive<Record<string, any>>({
    discounts: null,
    tips: null,
    subscribe: false,
    productOptionId: props.product.default_product_option.id,
    quantity: 1,
    subscriptionOptionId: 0,
});

updateDiscountsAndTips();

function addToCart() {
    if (loading.value) {
        return;
    }

    if (added.value) {
        added.value = false;
        quantityAdded.value = 0;
        return;
    }

    const params: Record<string, any> = {
        product_option_id: state.productOptionId,
        quantity: state.quantity,
    };

    if (state.subscribe) {
        params.subscription_option_id = state.subscriptionOptionId;
    }

    loading.value = true;

    addCartItem(params)
        .then((data) => {
            emitter.emit("cartItemUpdated", data);
            clearErrors();

            added.value = true;
            quantityAdded.value = state.quantity;
            openSideBar();
            pushAddToCartToDataLayer({
                product: props.product,
                subscribe: state.subscribe,
                quantity: state.quantity,
                source: `${props.product.name} Product Page`,
            });

            state.subscribe = false;
            state.quantity = 1;
        })
        .catch((error: any) => {
            if (
                typeof error.response !== "undefined" &&
                Object.prototype.hasOwnProperty.call(error.response, "data") &&
                Object.prototype.hasOwnProperty.call(
                    error.response.data,
                    "errors",
                )
            ) {
                const field = Object.keys(error.response.data.errors)[0];
                errors[field] = error.response.data.errors[field][0];
            }
        })
        .finally(() => (loading.value = false));
}

function openSideBar() {
    if (!props.recommendedProduct) {
        return emitter.emit("openCartSideBar");
    }

    emitter.emit("openProductRecommendationSideBar", {
        quantityAdded: state.quantity,
        subscriptionOptionId: state.subscribe
            ? state.subscriptionOptionId
            : null,
    });
}

function clearErrors() {
    for (const field in errors) {
        delete errors[field];
    }
}

function onErrorMaxQuantity(errorMaxQuantity: any) {
    clearErrors();
    Object.assign(errors, errorMaxQuantity);
}

function onQuantityUpdated(quantity: number) {
    state.quantity = quantity;
    clearErrors();
    updateDiscountsAndTips();
}

function onSubscribe(subscribe: boolean) {
    if (!subscribe) {
        state.subscriptionOptionId = 0;
    }

    if (subscribe && !state.subscribe && !state.subscriptionOptionId) {
        state.subscriptionOptionId =
            props.product.default_product_option.default_subscription_option;
    }

    state.subscribe = subscribe;
    clearErrors();
    updateDiscountsAndTips();
}

function onSubscriptionOptionUpdated(subscriptionOptionId: number) {
    state.subscriptionOptionId = subscriptionOptionId;
    onSubscribe(!!subscriptionOptionId);
}

function increaseQuantity(quantity: number) {
    onQuantityUpdated(state.quantity + quantity);
}

function updateDiscountsAndTips() {
    const params: Record<string, any> = {
        product_option_id: state.productOptionId,
        quantity: state.quantity,
    };

    if (state.subscribe) {
        params.subscription_option_id = state.subscriptionOptionId;
    }

    getProductOptions(params)
        .then((data) => {
            state.discounts = data.discounts;
            state.tips = data.tips;
        })
        .catch(() => {
            state.discounts = {};
            state.tips = {};
        });
}

onMounted(() => emitter.on("cartItemUpdated", () => updateDiscountsAndTips()));
</script>
